import { useState } from 'react'
import styled from 'styled-components'
import Layout from 'src/components/Layout'
import { useMarketingQueryParams } from 'src/utils/marketing'
import Link from 'next/link'
import Container from 'src/styles/Container'
import { H3, SectionHeading, TextBody } from 'src/styles/Typography'
import { StyledAnchor } from 'src/styles/Button'
import InsurersParade from 'src/components/Affiliate/InsurersParade'
import ValueProposal from 'src/components/Affiliate/ValueProposal'
import AffiliateHero from 'src/components/Affiliate/Hero'
import AffiliateFAQ from 'src/components/Home/FAQs'
import VerticalProviders from 'src/components/Vertical/Providers'
import RichTextResponse from 'src/components/RichText'
import TableOfContents from 'src/components/Article/TableOfContents'
import grabColor from 'src/utils/grabColor'
import SvgRxIcon from 'src/components/icons/RxIcon'

const flow = 'a'

const VerticalIndex = ({ verticals, vertical, verticalProviders }) => {
  const verticalSlug = vertical?.slug?.replace('-insurance', '')
  const [form, setForm] = useState({})

  const onReadyCallback = (filteredQueryParams) => {
    setForm((f) => ({
      ...f,
      ...filteredQueryParams,
    }))
  }

  const onDataLayer = (data) => {
    return {
      ...data,
      type: verticalSlug,
      vertical: verticalSlug,
      funnel: flow,
      step: 'landing-page-organic',
    }
  }

  useMarketingQueryParams({
    onReadyCallback,
    onDataLayer,
  })

  return (
    <Layout
      verticals={verticals}
      vertical={vertical}
      seo={vertical.seo}
      footer={{ noBorder: true }}
      header={{ govAgency: verticalSlug === 'medicare' ? true : false }}
    >
      <main>
        <AffiliateHero
          verticalSlug={verticalSlug}
          verticalTrueSlug={vertical.slug}
          hero={vertical.hero}
          shortDescription={vertical.shortDescription}
          title={vertical.title}
          queryParamList={form}
        />

        <InsurersParade verticalSlug={verticalSlug} />

        <RichTextWrapper>
          <Container>
            <TableOfContents>
              {(verticalSlug === 'health' || verticalSlug === 'medicare') && (
                <RxCTASection>
                  <SectionHeading>PolicyScout Member Benefits</SectionHeading>
                  <RxIconWrapper>
                    <SvgRxIcon />
                  </RxIconWrapper>
                  <H3>
                    Save up to $95 on each prescription with our <br />
                    <span>Free Rx Discount Card</span> <sup>*</sup>
                  </H3>
                  <TextBody>
                    PolicyScout has negotiated for you discounted Rx prices across 35,000
                    pharmacies nationally.
                  </TextBody>
                  <Link href="/rx/download" legacyBehavior>
                    <StyledAnchor width="241px" size="large">
                      Get Your Free Rx Card
                    </StyledAnchor>
                  </Link>
                </RxCTASection>
              )}
              <RichTextContainer>
                <RichTextInnerWrapper>
                  <RichTextResponse
                    richTextResponse={vertical.dashboardPage}
                    data={{
                      vertical: vertical.slug,
                      linkColor: 'brand.DEFAULT',
                      linkHover: 'brand.dark',
                    }}
                  />
                </RichTextInnerWrapper>
              </RichTextContainer>
            </TableOfContents>
          </Container>
        </RichTextWrapper>

        <VerticalProviders providers={verticalProviders} vertical={vertical} />

        <AffiliateFAQ faqList={vertical.dashboardFaQs} />

        <ValueProposal />
      </main>
    </Layout>
  )
}

const RxCTASection = styled.div`
  display: flex;
  align-items: center;
  padding: 3rem 0 2rem;
  margin: 1rem 0 0 3rem;
  border-top: 1px solid ${grabColor('borderContent')};

  ${SectionHeading} {
    display: none;
    margin: 0 auto 2rem;
    color: ${grabColor('brand.dark')};
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      display: block;
    }
  }

  ${TextBody} {
    display: none;
    text-align: center;
    padding: 0 1rem;
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      display: block;
    }
  }

  ${H3} {
    margin: 0 0 0 20px;
    br {
      display: none;
      @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: block;
      }
    }
    sup {
      color: ${grabColor('detail')};
    }
    span {
      color: ${grabColor('rx.DEFAULT')};
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      margin: 1rem auto;
      text-align: center;
      font-size: 24px;
      line-height: 36px;
    }
  }

  a {
    margin-left: auto;
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      margin: 1rem auto 0;
      width: 100%;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: column;
    margin: 1rem auto 3rem;
    padding: 2rem 1rem 2rem;
  }
`

const RxIconWrapper = styled.div`
  svg {
    width: 48px;
    height: 48px;
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      width: 72px;
      height: 72px;
    }
  }
`

const RichTextInnerWrapper = styled.div`
  margin-top: -2rem;
`

const RichTextWrapper = styled.div`
  background: ${grabColor('backgroundLight')};
  padding: 2rem 0;
`

const RichTextContainer = styled.div`
  max-width: 664px;
  width: 100%;
  margin: 3rem;

  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    margin: 3rem 0;
  }
`

export default VerticalIndex
